/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import {
    Checkbox,
    Heading,
    Accordion,
    AccordionContent,
    AccordionHeading,
    AccordionItem,
} from 'sarsaparilla';

import { SEARCH_FILTER_AGENCIES_VALUES } from '../../../constants/index';

type AgencyFilterItem = {
    key: string;
    hasCode: boolean;
    value: string;
    displayName: string;
    filters: { attribute: string; filterValue: string }[];
};

interface AgenciesFiltersProps {
    uiFilters: {
        agencies?: Record<string, unknown>;
    };
    onFilterToggle: (filterName: string, item: AgencyFilterItem) => void;
    /** Renders the checkboxes inside a collapsible accordion instead of under a heading */
    expandable?: boolean;
    className?: string;
}

/** Federal agency checkboxes. Every agency can be selected. */
export function AgenciesFilters({
    uiFilters,
    onFilterToggle,
    expandable = false,
    className,
}: AgenciesFiltersProps) {
    const selections = uiFilters.agencies ?? {};

    const checkboxGroup = (
        <div
            className="search-filters-double-type-wrap"
            role="group"
            aria-label="Agencies filters"
        >
            <legend className="rec-sr-only">Select agencies to filter on</legend>
            {SEARCH_FILTER_AGENCIES_VALUES.map((item: AgencyFilterItem) => {
                const name = item.hasCode
                    ? `${item.displayName} - ${item.key}`
                    : item.displayName;

                return (
                    <div className="search-filter-check-item" key={item.key}>
                        <Checkbox
                            onChange={() => onFilterToggle('agencies', item)}
                            id={item.key}
                            label={name}
                            gaTrackingId="494913253373"
                            value={item.value}
                            isChecked={Object.hasOwn(selections, item.key)}
                        />
                    </div>
                );
            })}
        </div>
    );

    if (expandable) {
        return (
            <div className="search-filters-accordion__container pb-0 my-0">
                <Accordion
                    hasIndentedBody={false}
                    className="search-filters-accordion__item"
                >
                    <AccordionItem>
                        <AccordionHeading className="search-filters-accordion__heading">
                            Federal Agencies Offering Reservations
                        </AccordionHeading>
                        <AccordionContent>{checkboxGroup}</AccordionContent>
                    </AccordionItem>
                </Accordion>
            </div>
        );
    }

    return (
        <div className={className}>
            <div className="search-filters-section-header rounded-layout">
                <Heading headingLevel={2} appearance="h5">
                    Federal Agencies Offering Reservations
                </Heading>
            </div>
            {checkboxGroup}
        </div>
    );
}
